import { forwardRef, useMemo, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import type { CallsReport } from '@mangovoice/types'

import { type ReportingApi, ReportingApiProvider } from '../api'
import InboundAnswerRateTable from '../components/InboundAnswerRateTable'
import RechartsBarChart from '../components/RechartsBarChart'
import { ReportingDashboardShell } from '../components/ReportingDashboardShell'
import type { ViewType } from '../components/ViewToggle'
import { useDateContext, useIsDateRangeTooLarge } from '../hooks/useDateContext'
import { type DashboardExportHandle, useExportCsv } from '../hooks/useExportCsv'
import { useGetAnswerRate } from '../hooks/useGetAnswerRate'
import {
  ReportingI18nProvider,
  type ReportingTranslationLike,
  useReportingI18n,
} from '../i18n'
import type { DateRange, ReportingPhoneNumber } from '../types'
import { DEFAULT_METRIC_VALUE } from '../types'
import { getGroupBy } from '../utils/dashboardUtils'
import {
  bucketSegmentsByDay,
  formatUtcDateTimeForTimeZone,
} from '../utils/LocalTimeHelper'

interface InboundAnswerRateDashboardInnerProps {
  sharedDateRange: DateRange
  selectedPbxId: number
  timeZone?: string
  selectedPhoneNumbers: ReportingPhoneNumber[]
  onExportAvailabilityChange?: (canExport: boolean) => void
  toast?: ToastLike
}

export type InboundAnswerRateDashboardHandle = DashboardExportHandle

const InboundAnswerRateDashboardInner = forwardRef<
  InboundAnswerRateDashboardHandle,
  InboundAnswerRateDashboardInnerProps
>(
  (
    {
      sharedDateRange,
      selectedPbxId,
      timeZone,
      selectedPhoneNumbers,
      onExportAvailabilityChange,
      toast,
    },
    ref,
  ) => {
    const { text } = useReportingI18n()
    const { groupBy, groupByTitle } = getGroupBy(sharedDateRange)
    const [viewType, setViewType] = useState<ViewType>('chart')

    const dateContext = useDateContext(sharedDateRange, timeZone)
    const isDateRangeTooLarge = useIsDateRangeTooLarge(sharedDateRange)

    const {
      data: answerRateData,
      isFetching: isAnswerRateLoading,
      error: answerRateError,
    } = useGetAnswerRate({
      request: dateContext
        ? {
            start: dateContext.startDateUtc,
            end: dateContext.endDateUtc,
            pbx: selectedPbxId.toString(),
            ...(selectedPhoneNumbers.length > 0 && {
              phone_number: selectedPhoneNumbers.map((p) => p.number),
            }),
            group_by: 'hour',
          }
        : undefined,
      enabled: !!dateContext && !isDateRangeTooLarge,
    })

    const transformedCallsReport: CallsReport | null = useMemo(() => {
      if (!answerRateData?.segments?.length || !dateContext || !timeZone)
        return null

      const displaySegments =
        groupBy === 'date'
          ? bucketSegmentsByDay(answerRateData.segments, timeZone)
          : [...answerRateData.segments].sort((a, b) => {
              const dateA = a.dateRange?.start || dateContext.startDateUtc
              const dateB = b.dateRange?.start || dateContext.startDateUtc
              return new Date(dateA).getTime() - new Date(dateB).getTime()
            })

      return {
        meta: {
          groupBy: [groupBy],
          filters: {
            startDate: dateContext.startDateUtc,
            endDate: dateContext.endDateUtc,
            timezone: timeZone,
            pbxIds: [selectedPbxId],
            phoneNumbers:
              selectedPhoneNumbers.length > 0
                ? selectedPhoneNumbers.map((p) => p.number)
                : undefined,
            direction: 'inbound',
          },
        },
        data: displaySegments.map((segment) => {
          const date = segment.dateRange?.start || dateContext.startDateUtc

          return {
            date,
            Answered: segment.counts.answered ?? DEFAULT_METRIC_VALUE,
            Missed: segment.counts.rejected ?? DEFAULT_METRIC_VALUE,
          }
        }),
      }
    }, [
      answerRateData,
      groupBy,
      dateContext,
      selectedPbxId,
      selectedPhoneNumbers,
      timeZone,
    ])

    const totals = useMemo(() => {
      if (!answerRateData?.segments?.length) {
        return { answered: 0, missed: 0, total: 0, answerRate: 0 }
      }

      let answered = 0
      let missed = 0

      answerRateData.segments.forEach((segment) => {
        answered += segment.counts?.answered ?? 0
        missed += segment.counts?.rejected ?? 0
      })

      const total = answered + missed
      const answerRate = total > 0 ? Math.round((answered / total) * 100) : 0

      return { answered, missed, total, answerRate }
    }, [answerRateData?.segments])

    const csvRows = useMemo(() => {
      if (!dateContext || !answerRateData?.segments?.length) {
        return []
      }

      const h = {
        startDateTime: text(
          'dashboards.csvExport.headers.inboundAnswerRate.startDateTime',
          'Start Date/Time',
        ),
        endDateTime: text(
          'dashboards.csvExport.headers.inboundAnswerRate.endDateTime',
          'End Date/Time',
        ),
        answeredCalls: text(
          'dashboards.csvExport.headers.inboundAnswerRate.answeredCalls',
          'Answered Calls',
        ),
        missedCalls: text(
          'dashboards.csvExport.headers.inboundAnswerRate.missedCalls',
          'Missed Calls',
        ),
        totalCalls: text(
          'dashboards.csvExport.headers.inboundAnswerRate.totalCalls',
          'Total Calls',
        ),
        answerRatePercent: text(
          'dashboards.csvExport.headers.inboundAnswerRate.answerRatePercent',
          'Answer Rate %',
        ),
      }

      return answerRateData.segments.map((segment) => {
        const answered = segment.counts.answered ?? 0
        const missed = segment.counts.rejected ?? 0
        const totalCalls = answered + missed

        return {
          [h.startDateTime]: formatUtcDateTimeForTimeZone(
            segment.dateRange?.start || dateContext.startDateUtc,
            timeZone,
          ),
          [h.endDateTime]: formatUtcDateTimeForTimeZone(
            segment.dateRange?.end || dateContext.endDateUtc,
            timeZone,
          ),
          [h.answeredCalls]: answered,
          [h.missedCalls]: missed,
          [h.totalCalls]: totalCalls,
          [h.answerRatePercent]:
            totalCalls > 0 ? Math.round((answered / totalCalls) * 100) : 0,
        }
      })
    }, [answerRateData?.segments, dateContext, text, timeZone])

    useExportCsv({
      ref,
      csvRows,
      filenamePrefix: 'inbound_answer_rate',
      isLoading: isAnswerRateLoading,
      hasError: !!answerRateError,
      toast,
      onExportAvailabilityChange,
    })

    const counters = useMemo(
      () => [
        {
          value: totals.answerRate,
          label: text(
            'inboundAnswerRate.counters.answerRate.title',
            'Answer Rate',
          ),
          subtitle: text(
            'inboundAnswerRate.counters.answerRate.subtitle',
            'Percentage of calls answered',
          ),
          isPercentage: true,
        },
        {
          value: totals.total,
          label: text(
            'inboundAnswerRate.counters.totalCalls.title',
            'Total Calls',
          ),
          subtitle: text(
            'inboundAnswerRate.counters.totalCalls.subtitle',
            'Total inbound calls',
          ),
        },
        {
          value: totals.answered,
          label: text(
            'inboundAnswerRate.counters.answered.title',
            'Answered Calls',
          ),
          subtitle: text(
            'inboundAnswerRate.counters.answered.subtitle',
            'Total answered calls',
          ),
        },
        {
          value: totals.missed,
          label: text(
            'inboundAnswerRate.counters.missed.title',
            'Missed Calls',
          ),
          subtitle: text(
            'inboundAnswerRate.counters.missed.subtitle',
            'Total missed calls',
          ),
        },
      ],
      [totals, text],
    )

    return (
      <ReportingDashboardShell
        timeZone={timeZone}
        dateContextReady={!!dateContext}
        isDateRangeTooLarge={isDateRangeTooLarge}
        isLoading={isAnswerRateLoading}
        hasError={!!answerRateError}
        hasData={!!transformedCallsReport}
        viewType={viewType}
        onViewTypeChange={setViewType}
        counters={counters}
        i18nKeys={{
          invalidDateRange: 'inboundAnswerRate.invalidDateRange',
          gettingPbx: 'dashboards.gettingPbx',
          dateRangeTooLarge: 'inboundAnswerRate.dateRangeTooLarge',
          dateRangeAdjustPrompt: 'inboundAnswerRate.dateRangeAdjustPrompt',
          errorText: 'inboundAnswerRate.dashboard.errorText',
          noDataText: 'inboundAnswerRate.dashboard.noDataText',
        }}
        chartContent={
          transformedCallsReport ? (
            <RechartsBarChart
              chartData={transformedCallsReport}
              timeZone={timeZone}
              color={['blue.solid', 'red.solid']}
              title={text(
                'inboundAnswerRate.rechartTitle',
                'Calls Per {{groupByTitle}}',
                { groupByTitle },
              )}
              height={350}
              hasModalButton={false}
              showLegend={false}
              hideWrapper
              barWidth={24}
              barRadius={[8, 8, 8, 8]}
              showGrid={false}
              minBarSize={2}
              seriesLabels={{
                Answered: text(
                  'inboundAnswerRate.metrics.answered',
                  'Answered',
                ),
                Missed: text('inboundAnswerRate.metrics.missed', 'Missed'),
              }}
            />
          ) : null
        }
        tableContent={
          dateContext ? (
            <InboundAnswerRateTable
              startDate={dateContext.startDateUtc}
              endDate={dateContext.endDateUtc}
              pbxId={selectedPbxId}
              selectedPhoneNumbers={selectedPhoneNumbers}
            />
          ) : null
        }
      />
    )
  },
)

export interface InboundAnswerRateDashboardProps extends InboundAnswerRateDashboardInnerProps {
  api: ReportingApi
  t?: ReportingTranslationLike
  locale?: string
}

export const InboundAnswerRateDashboard = forwardRef<
  InboundAnswerRateDashboardHandle,
  InboundAnswerRateDashboardProps
>(({ api, t, locale, ...rest }, ref) => {
  return (
    <ReportingApiProvider api={api}>
      <ReportingI18nProvider t={t} locale={locale}>
        <InboundAnswerRateDashboardInner ref={ref} {...rest} />
      </ReportingI18nProvider>
    </ReportingApiProvider>
  )
})

export default InboundAnswerRateDashboard
